/**
 * モーショングラフィックス生成の品質モード (docs/features/brand-cut-motion-graphics-plan.md §10.10.1)。
 * 2026-10-01 利用者決定で 3 段にし、2026-10-02 利用者決定で呼び名を「ライト / スタンダード / ダメ出し」に、
 * 選択肢と既定を経路ごとに決めた (内部の値 'speed' / 'draft' / 'loop' は変えない):
 *
 * - 'speed': ライト。軽い表現 (光・粒子・発光なし) で短時間に作る (AI の思考を抑え、試写も軽い版)。全編は 1 本 100 ポイントの定額
 * - 'draft': スタンダード。初稿 → 試写 → 描画
 * - 'loop' : ダメ出し。AI が採点と修正を繰り返す (最大 3 回修正・時間が 2〜4 倍かかる・料金はスタンダードと同じ)
 *
 * 経路 (MOTION_QUALITY_ROUTES):
 * - brandCut (ブランドカット): スタンダード / ダメ出し。既定ダメ出し (2026-10-05 利用者決定。料金は同じで、短尺なので採点と修正を既定で回す。ライトの出番がない)
 * - layered (プロンプト入力モードのレイヤー素材): ライト / スタンダード / ダメ出し。既定スタンダード
 * - scene (シーンごとの動画生成): ライト / スタンダード / ダメ出し。既定スタンダード
 * - whole (全編モーション): ライト / スタンダード / ダメ出し。既定ライト
 *
 * 選択は経路ごとに localStorage に覚える (同じ経路の選択が同じ画面に複数あっても (シーンごとの動画欄など) 表示が揃うよう購読型のストア)。
 */
import { useCallback, useSyncExternalStore } from 'react';

export type MotionQuality = 'speed' | 'draft' | 'loop';

export const MOTION_QUALITIES: readonly MotionQuality[] = ['speed', 'draft', 'loop'];

/** 生成の経路 (選択肢と既定が違う) */
export type MotionQualityRoute = 'brandCut' | 'layered' | 'scene' | 'whole';

export const MOTION_QUALITY_ROUTES: Record<MotionQualityRoute, { options: readonly MotionQuality[]; defaultQuality: MotionQuality }> = {
  brandCut: { options: ['draft', 'loop'], defaultQuality: 'loop' },
  layered: { options: MOTION_QUALITIES, defaultQuality: 'draft' },
  scene: { options: MOTION_QUALITIES, defaultQuality: 'draft' },
  whole: { options: MOTION_QUALITIES, defaultQuality: 'speed' },
};

/** localStorage のキーの接頭辞。実際のキーは `${接頭辞}.${経路}`。値は 'speed' | 'draft' | 'loop' (それ以外・未設定は経路の既定) */
export const MOTION_QUALITY_STORAGE_KEY_PREFIX = 'motion_generation_quality';
export function motionQualityStorageKey(route: MotionQualityRoute): string {
  return `${MOTION_QUALITY_STORAGE_KEY_PREFIX}.${route}`;
}

/** 選択肢の文言 (全経路で共通) */
export const MOTION_QUALITY_LABELS: Record<MotionQuality, string> = {
  speed: 'ライト',
  draft: 'スタンダード',
  loop: 'ダメ出し',
};
export const MOTION_QUALITY_DESCRIPTIONS: Record<MotionQuality, string> = {
  speed: '軽い表現 (光・粒子・影なし) で短時間に作ります',
  draft: '光・粒子・影などの表現を使って仕上げます',
  loop: 'AI が採点と修正を繰り返します (最大 3 回)。時間が 2〜4 倍かかります',
};
/** 選択の見出し */
export const MOTION_QUALITY_HEADING = '仕上げ方';

function isMotionQuality(value: unknown): value is MotionQuality {
  return value === 'speed' || value === 'draft' || value === 'loop';
}

const listeners = new Set<() => void>();
// localStorage が使えない環境で、その場の選択だけは保つための控え (経路ごと)
const memoryFallback: Partial<Record<MotionQualityRoute, MotionQuality>> = {};

export function readMotionQuality(route: MotionQualityRoute): MotionQuality {
  const { options, defaultQuality } = MOTION_QUALITY_ROUTES[route];
  try {
    const value = localStorage.getItem(motionQualityStorageKey(route));
    return isMotionQuality(value) && options.includes(value) ? value : defaultQuality;
  } catch {
    return defaultQuality;
  }
}

export function writeMotionQuality(route: MotionQualityRoute, quality: MotionQuality): void {
  try {
    localStorage.setItem(motionQualityStorageKey(route), quality);
  } catch {
    /* 保存できなくても (プライベートモード等) 画面の状態は変える */
    memoryFallback[route] = quality;
  }
  for (const l of listeners) l();
}

function snapshot(route: MotionQualityRoute): MotionQuality {
  return memoryFallback[route] ?? readMotionQuality(route);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** 経路の品質モードの選択の状態と setter (同じ経路の選択で共有)。 */
export function useMotionQuality(route: MotionQualityRoute): [MotionQuality, (next: MotionQuality) => void] {
  const value = useSyncExternalStore(
    subscribe,
    () => snapshot(route),
    () => MOTION_QUALITY_ROUTES[route].defaultQuality,
  );
  const set = useCallback((next: MotionQuality) => {
    delete memoryFallback[route];
    writeMotionQuality(route, next);
  }, [route]);
  return [value, set];
}

/** テスト用: 控えとリスナーを初期化する */
export function __resetMotionQualityForTest(): void {
  for (const route of Object.keys(memoryFallback) as MotionQualityRoute[]) delete memoryFallback[route];
}

/** 生成を送る瞬間の設定 (選択と同じ値。localStorage が使えないときはその場の選択)。 */
export function currentMotionQuality(route: MotionQualityRoute): MotionQuality {
  return snapshot(route);
}

/**
 * その経路で送る品質。allowed に無い値 (全編でライトの料金が未設定のときのライト等) は fallback (経路の既定) に、
 * それも選べなければ allowed の先頭に寄せる。
 */
export function motionQualityFor(quality: MotionQuality, allowed: readonly MotionQuality[], fallback: MotionQuality = 'draft'): MotionQuality {
  if (allowed.includes(quality)) return quality;
  if (allowed.includes(fallback)) return fallback;
  return allowed[0] ?? 'draft';
}
